import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { portfolioImages } from '../mock';

const Portfolio = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [showAllGallery, setShowAllGallery] = useState(false);
  const [showAllWedding, setShowAllWedding] = useState(false);
  const [showAllFamily, setShowAllFamily] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get featured gallery images (6 for desktop, 3 for mobile)
  const featuredGalleryImages = portfolioImages.galleryWork
    .filter(img => img.featured)
    .slice(0, isMobile ? 3 : 6);
  
  // Get all gallery images
  const allGalleryImages = portfolioImages.galleryWork;

  // Get featured wedding images (6 for desktop, 3 for mobile)
  const featuredWeddingImages = portfolioImages.weddingEngagement
    .filter(img => img.featured)
    .slice(0, isMobile ? 3 : 6);
  
  // Get all wedding images
  const allWeddingImages = portfolioImages.weddingEngagement;

  // Get featured family images (6 for desktop, 3 for mobile)
  const featuredFamilyImages = portfolioImages.familyVacation
    .filter(img => img.featured)
    .slice(0, isMobile ? 3 : 6);
  
  // Get all family images
  const allFamilyImages = portfolioImages.familyVacation;

  return (
    <section id="portfolio" className="py-20 bg-gradient-to-b from-slate-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12 sm:mb-16 px-4">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-3 sm:mb-4 bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">
            Portfolio
          </h2>
          <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto">
            Explore my work across different photography styles
          </p>
        </div>

        <Tabs defaultValue="galleryWork" className="w-full">
          <TabsList className="grid w-full max-w-3xl mx-auto grid-cols-3 gap-1 sm:gap-2 mb-8 sm:mb-12 h-auto p-1 sm:p-2 bg-slate-100/80 backdrop-blur-sm">
            <TabsTrigger
              value="galleryWork"
              onClick={() => setShowAllGallery(true)}
              className="text-[10px] sm:text-sm md:text-base py-2 sm:py-3 px-0.5 sm:px-2 leading-tight data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-teal-500 data-[state=active]:text-white transition-all duration-300"
            >
              <span className="block">Gallery</span>
              <span className="block">Work</span>
            </TabsTrigger>
            <TabsTrigger
              value="weddingEngagement"
              onClick={() => setShowAllWedding(true)}
              className="text-[10px] sm:text-sm md:text-base py-2 sm:py-3 px-0.5 sm:px-2 leading-tight data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-teal-500 data-[state=active]:text-white transition-all duration-300"
            >
              <span className="block">Wedding &</span>
              <span className="block">Engagement</span>
            </TabsTrigger>
            <TabsTrigger
              value="familyVacation"
              onClick={() => setShowAllFamily(true)}
              className="text-[10px] sm:text-sm md:text-base py-2 sm:py-3 px-0.5 sm:px-2 leading-tight data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-teal-500 data-[state=active]:text-white transition-all duration-300"
            >
              <span className="block">Family</span>
              <span className="block">Vacation</span>
            </TabsTrigger>
          </TabsList>

          {/* Gallery Work */}
          <TabsContent value="galleryWork" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(showAllGallery ? allGalleryImages : featuredGalleryImages).map((image) => (
                <Card
                  key={image.id}
                  className="group overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer"
                  onClick={() => setSelectedImage(image)}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={image.url}
                      alt={image.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <h3 className="text-white text-xl font-semibold">{image.title}</h3>
                        <p className="text-cyan-200 text-sm">{image.category}</p>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            
            {/* View All Button */}
            {!showAllGallery && (
              <div className="flex justify-center mt-8">
                <Button
                  onClick={() => setShowAllGallery(true)}
                  className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  View All Gallery Work ({allGalleryImages.length} photos)
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Wedding & Engagement */}
          <TabsContent value="weddingEngagement" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(showAllWedding ? allWeddingImages : featuredWeddingImages).map((image) => (
                <Card
                  key={image.id}
                  className="group overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer"
                  onClick={() => setSelectedImage(image)}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={image.url}
                      alt={image.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <h3 className="text-white text-xl font-semibold">{image.title}</h3>
                        <p className="text-cyan-200 text-sm">{image.category}</p>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            
            {/* View All Button */}
            {!showAllWedding && (
              <div className="flex justify-center mt-8">
                <Button
                  onClick={() => setShowAllWedding(true)}
                  className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  View All Wedding & Engagement ({allWeddingImages.length} photos)
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Family Vacation */}
          <TabsContent value="familyVacation" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(showAllFamily ? allFamilyImages : featuredFamilyImages).map((image) => (
                <Card
                  key={image.id}
                  className="group overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer"
                  onClick={() => setSelectedImage(image)}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={image.url}
                      alt={image.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <h3 className="text-white text-xl font-semibold">{image.title}</h3>
                        <p className="text-cyan-200 text-sm">{image.category}</p>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            
            {/* View All Button */}
            {!showAllFamily && (
              <div className="flex justify-center mt-8">
                <Button
                  onClick={() => setShowAllFamily(true)}
                  className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  View All Family Vacation ({allFamilyImages.length} photos)
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-6xl max-h-[90vh]">
            <img
              src={selectedImage.url}
              alt={selectedImage.title}
              className="max-w-full max-h-[90vh] object-contain"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
              <h3 className="text-white text-2xl font-semibold">{selectedImage.title}</h3>
              <p className="text-cyan-200">{selectedImage.category}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Portfolio;
